/** @jsxImportSource @emotion/react */
import { getBaseLayerId } from '@app/domains/layers/utils/layers.utils'
import {
  SUITABILITY_GRADES,
  SuitabilityCreateForm,
  SuitabilityGrade,
} from '@app/domains/suitability/types'
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material'
import { useAtomValue } from 'jotai'
import { useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import ReactMarkdown from 'react-markdown'
import { useNavigate, useParams } from 'react-router-dom'
import { enabledUseCasesAtom } from '../../useCases/state/useCasesAtom'
import { useFetchLayerSuitability } from '../hooks/useFetchLayerSuitability'
import { useUpsertLayerSuitability } from '../hooks/useUpsertLayerSuitability'
import * as styles from './SuitabilityCreate.styles'

export const SuitabilityEdit = () => {
  const { layerId: rawLayerId, use_case } = useParams<{ layerId: string; use_case: string }>()
  const layerId = rawLayerId ? getBaseLayerId(rawLayerId) : undefined
  const [mode, setMode] = useState<'write' | 'preview'>('write')

  const enabledUseCases = useAtomValue(enabledUseCasesAtom)
  const { data: suitabilities = [] } = useFetchLayerSuitability(layerId ?? '')
  const { mutate: updateLayerSuitability, isPending } = useUpsertLayerSuitability(layerId ?? '')
  const navigate = useNavigate()

  const {
    control,
    handleSubmit,
    watch,
    reset,
    formState: { errors, isDirty },
  } = useForm<SuitabilityCreateForm>({
    defaultValues: {
      useCaseSlug: '',
      grade: 'C',
      statementMd: '',
    },
  })

  const selectedSlug = watch('useCaseSlug')
  const statementMd = watch('statementMd')

  // Prefill when use case selection changes
  useEffect(() => {
    const existing = suitabilities.find((s) => s.use_case_slug === use_case)
    if (existing) {
      reset({
        useCaseSlug: existing.use_case_slug,
        grade: existing.suitability_grade as SuitabilityGrade,
        statementMd: existing.suitability_statement_md ?? '',
      })
    }
  }, [use_case, suitabilities, reset])

  useEffect(() => {
    // Warn user of unsaved changes when trying to leave the page
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (!isDirty) return

      e.preventDefault()
      e.returnValue = '' // required for Chrome
    }

    window.addEventListener('beforeunload', handleBeforeUnload)

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [isDirty])

  const onSubmit = (values: SuitabilityCreateForm) => {
    if (!layerId) return
    updateLayerSuitability({
      useCaseSlug: values.useCaseSlug,
      payload: {
        suitability_grade: values.grade,
        suitability_statement_md: values.statementMd || undefined,
      },
    })
  }

  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)} css={styles.formRoot}>
      <Stack spacing={2} css={styles.formStack}>
        <Box css={styles.fieldsSection}>
          <Box css={styles.topFieldsGrid}>
            <Controller
              name="useCaseSlug"
              control={control}
              rules={{ required: 'Use case is required' }}
              render={({ field }) => (
                <FormControl
                  size="small"
                  fullWidth
                  error={!!errors.useCaseSlug}
                  css={styles.fieldControl}
                >
                  <InputLabel id="edit-use-case-label" shrink>
                    Use case
                  </InputLabel>
                  <Select
                    disabled
                    {...field}
                    labelId="edit-use-case-label"
                    label="Use case"
                    value={field.value ?? ''}
                    renderValue={(selected) => {
                      if (!selected) {
                        return 'Select a use case'
                      }

                      return enabledUseCases.find((uc) => uc.slug === selected)?.name ?? selected
                    }}
                  >
                    {enabledUseCases.map((uc) => (
                      <MenuItem key={uc.slug} value={uc.slug}>
                        {uc.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.useCaseSlug && (
                    <FormHelperText>{errors.useCaseSlug.message}</FormHelperText>
                  )}
                </FormControl>
              )}
            />

            <Controller
              name="grade"
              control={control}
              rules={{ required: 'Grade is required' }}
              render={({ field }) => (
                <TextField
                  {...field}
                  select
                  fullWidth
                  size="small"
                  label="Suitability grade"
                  error={!!errors.grade}
                  helperText={errors.grade?.message}
                  css={styles.fieldControl}
                >
                  {SUITABILITY_GRADES.map((g) => (
                    <MenuItem key={g} value={g}>
                      {g}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
          </Box>

          {selectedSlug && (
            <>
              <Box css={styles.modeSection}>
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={mode}
                  onChange={(_, val) => val && setMode(val)}
                  css={styles.modeToggleGroup}
                >
                  <ToggleButton value="write">Write</ToggleButton>
                  <ToggleButton value="preview">Preview</ToggleButton>
                </ToggleButtonGroup>

                {mode === 'write' ? (
                  <Controller
                    name="statementMd"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        multiline
                        minRows={8}
                        label="Suitability statement (Markdown)"
                        css={styles.statementField}
                      />
                    )}
                  />
                ) : (
                  <Box css={styles.previewPanel}>
                    {statementMd ? (
                      <ReactMarkdown>{statementMd}</ReactMarkdown>
                    ) : (
                      <Box css={styles.previewEmptyState}>Nothing to preview.</Box>
                    )}
                  </Box>
                )}
              </Box>

              <Box css={styles.actionsRow}>
                <Button
                  onClick={() => navigate(-1)}
                  type="button"
                  variant="outlined"
                  disabled={!layerId || isPending}
                  size="medium"
                >
                  Back
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={!layerId || isPending}
                  css={styles.submitButton}
                  size="small"
                >
                  Update suitability
                </Button>
              </Box>
            </>
          )}
        </Box>
      </Stack>
    </Box>
  )
}
